import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { filterUploadableFiles } from '../../services/uploadErrors.ts';
import {
  MessageSquare,
  X,
  Minus,
  Maximize2,
  Send,
  Paperclip,
  Users,
  CheckCheck,
  Search,
  ChevronLeft,
  Sparkles,
  Phone,
  Video,
  Hash,
  AtSign,
  ExternalLink,
  FileText,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';
import { useRealtime } from '../../context/RealtimeContext.tsx';
import { useCall } from '../../context/CallContext.tsx';
import { useChatUnread } from '../../context/ChatUnreadContext.tsx';
import { ChatChannel, ChatMessage } from '../../../shared/types.ts';
import { LinkifiedText } from '../common/LinkifiedText.tsx';

interface FloatingChatPopupProps {
  currentPage: string;
  onOpenFullChat: (channelId?: string) => void;
}

interface OpenDockTab {
  channelId: string;
  title: string;
  subtitle?: string;
  isDirect: boolean;
  isTeamChannel?: boolean;
  avatarUrl?: string;
  targetUserId?: string;
  minimized: boolean;
  unread: number;
}

export const FloatingChatPopup: React.FC<FloatingChatPopupProps> = ({
  currentPage,
  onOpenFullChat,
}) => {
  const { user, token } = useAuth();
  const { onlineUserIds, subscribe } = useRealtime();
  const { startDirectCall, startGroupCall } = useCall();
  const { totalUnread: serverUnread, unreadByChannel, refresh: refreshChatUnread } = useChatUnread();

  const [isLauncherOpen, setIsLauncherOpen] = useState(false);
  const [openTabs, setOpenTabs] = useState<OpenDockTab[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const [channels, setChannels] = useState<ChatChannel[]>([]);
  const [messagesByChannel, setMessagesByChannel] = useState<Record<string, ChatMessage[]>>({});
  const [loadingChannelIds, setLoadingChannelIds] = useState<string[]>([]);
  const [loadErrors, setLoadErrors] = useState<Record<string, string>>({});
  const [messageInput, setMessageInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState<
    Array<{ fileName: string; fileSize: number; mimeType: string; url: string }>
  >([]);
  const [inspectedTask, setInspectedTask] = useState<any | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const channelsRef = useRef<ChatChannel[]>([]);
  channelsRef.current = channels;
  const openTabsRef = useRef<OpenDockTab[]>([]);
  openTabsRef.current = openTabs;
  const activeTabIdRef = useRef<string | null>(null);
  activeTabIdRef.current = activeTabId;

  const isGuest = user?.role === 'guest';
  const isViewer = user?.role === 'viewer';

  const fetchChannels = useCallback(async (): Promise<ChatChannel[]> => {
    if (!token || isGuest) return [];
    try {
      const res = await fetch('/api/chat/channels', {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
      });
      if (res.ok) {
        const data = await res.json();
        const loaded: ChatChannel[] = data.channels || [];
        setChannels(loaded);
        return loaded;
      }
    } catch (err) {
      console.error('FloatingChatPopup failed to fetch channels:', err);
    }
    return [];
  }, [token, user?.id, isGuest]);

  const fetchChannelMessages = useCallback(
    async (channelId: string, markRead = true) => {
      if (!token || !channelId || isGuest) return;
      setLoadingChannelIds((prev) => (prev.includes(channelId) ? prev : [...prev, channelId]));
      try {
        const res = await fetch(
          `/api/chat/channels/${channelId}/messages?limit=50&markRead=${markRead ? 'true' : 'false'}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
            },
          }
        );
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          setLoadErrors((prev) => ({ ...prev, [channelId]: body.error || `Could not load messages (HTTP ${res.status}).` }));
        }
        if (res.ok) {
          const data = await res.json();
          setLoadErrors((prev) => {
            if (!prev[channelId]) return prev;
            const { [channelId]: _gone, ...rest } = prev;
            return rest;
          });
          setMessagesByChannel((prev) => ({
            ...prev,
            [channelId]: data.messages || [],
          }));
          if (markRead) {
            refreshChatUnread();
            setChannels((prev) =>
              prev.map((c) => (c.id === channelId ? { ...c, unreadCount: 0 } : c))
            );
            setOpenTabs((prev) =>
              prev.map((t) => (t.channelId === channelId ? { ...t, unread: 0 } : t))
            );
          }
        }
      } catch (err) {
        console.error('FloatingChatPopup failed to fetch messages:', err);
        setLoadErrors((prev) => ({ ...prev, [channelId]: 'Could not load messages. Check your connection and try again.' }));
      } finally {
        setLoadingChannelIds((prev) => prev.filter((id) => id !== channelId));
      }
    },
    [token, user?.id, isGuest, refreshChatUnread]
  );

  useEffect(() => {
    if (token && !isGuest) {
      fetchChannels();
    }
  }, [token, isGuest, fetchChannels]);

  const openOrFocusTab = useCallback(
    (tab: Omit<OpenDockTab, 'minimized' | 'unread'>) => {
      setOpenTabs((prev) => {
        const exists = prev.find((t) => t.channelId === tab.channelId);
        if (exists) {
          return prev.map((t) =>
            t.channelId === tab.channelId ? { ...t, ...tab, minimized: false, unread: 0 } : t
          );
        }
        // Keep up to 4 docked tabs
        const trimmed = prev.length >= 4 ? prev.slice(prev.length - 3) : prev;
        return [...trimmed, { ...tab, minimized: false, unread: 0 }];
      });
      setActiveTabId(tab.channelId);
      setIsLauncherOpen(false);
      fetchChannelMessages(tab.channelId, true);
      setTimeout(() => inputRef.current?.focus(), 80);
    },
    [fetchChannelMessages]
  );

  // Listen for global 'open-floating-chat' events from Team Cards, Member Profile Popovers, Team Header, etc.
  useEffect(() => {
    const handleOpenFloatingChat = async (e: Event) => {
      if (isGuest) return;
      const detail = (e as CustomEvent).detail || {};
      const { channelId, userId, userName, userAvatar, userRole, channelName } = detail;

      // Case 1: Open specific channel (e.g., #team-handle channel)
      if (channelId) {
        let currentList = channelsRef.current;
        let found = currentList.find((c) => c.id === channelId);
        if (!found) {
          currentList = await fetchChannels();
          found = currentList.find((c) => c.id === channelId);
        }
        const isTeam = channelId.startsWith('chan-team-');
        openOrFocusTab({
          channelId,
          title: found?.name || channelName || 'Team Chat',
          subtitle: isTeam ? 'Team Channel' : found?.type === 'direct' ? 'Direct Message' : 'Group Chat',
          isDirect: found?.type === 'direct',
          isTeamChannel: isTeam,
        });
        return;
      }

      // Case 2: Open 1-on-1 DM with a specific userId
      if (userId && token) {
        if (isViewer) return;
        try {
          const res = await fetch('/api/chat/channels', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
              ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
            },
            body: JSON.stringify({
              type: 'direct',
              targetUserId: userId,
            }),
          });
          if (res.ok) {
            const data = await res.json();
            const dmChannelId = data.channelId;
            await fetchChannels();
            openOrFocusTab({
              channelId: dmChannelId,
              title: userName || 'Direct Message',
              subtitle: userRole || 'Direct Message',
              isDirect: true,
              avatarUrl: userAvatar,
              targetUserId: userId,
            });
          }
        } catch (err) {
          console.error('Failed to initialize 1-on-1 DM:', err);
        }
      }
    };

    window.addEventListener('open-floating-chat', handleOpenFloatingChat);
    return () => window.removeEventListener('open-floating-chat', handleOpenFloatingChat);
  }, [token, user?.id, isGuest, isViewer, fetchChannels, openOrFocusTab]);

  // Listen for global 'open-task-by-code' events when clicking a task key pill (e.g., TSK-1001, FE-1)
  useEffect(() => {
    const handleOpenTaskByCode = async (e: Event) => {
      const code = (e as CustomEvent).detail?.code;
      if (!code || !token) return;
      try {
        const res = await fetch(`/api/tasks/${encodeURIComponent(code)}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
          },
        });
        if (res.ok) {
          const data = await res.json();
          if (data.task) {
            setInspectedTask(data.task);
          }
        }
      } catch (err) {
        console.warn('Could not load task for pill:', code);
      }
    };

    window.addEventListener('open-task-by-code', handleOpenTaskByCode);
    return () => window.removeEventListener('open-task-by-code', handleOpenTaskByCode);
  }, [token, user?.id]);

  // Listen for global 'start-call' events from Team Cards, Member Profile Popovers, Team Header, etc.
  useEffect(() => {
    const handleGlobalStartCall = (e: Event) => {
      if (isGuest || isViewer) return;
      const detail = (e as CustomEvent).detail || {};
      const withVideo = detail.callType === 'video';
      if (detail.targetUserId) {
        startDirectCall(
          {
            id: detail.targetUserId,
            name: detail.targetUserName || 'Team Member',
            avatarUrl: detail.targetUserAvatar,
            role: detail.targetUserRole,
          },
          withVideo,
          detail.channelId
        );
      } else if (detail.channelId) {
        startGroupCall(
          {
            id: detail.channelId,
            name: detail.channelName || 'Team Huddle',
          },
          withVideo
        );
      }
    };

    window.addEventListener('start-call', handleGlobalStartCall);
    return () => window.removeEventListener('start-call', handleGlobalStartCall);
  }, [isGuest, isViewer, startDirectCall, startGroupCall]);

  // Safe online presence check
  const isUserOnline = (userId?: string | null): boolean => {
    if (!userId || !onlineUserIds) return false;
    if (onlineUserIds instanceof Set) {
      return onlineUserIds.has(userId);
    }
    if (Array.isArray(onlineUserIds)) {
      return (onlineUserIds as string[]).includes(userId);
    }
    return false;
  };

  // A message from someone else adds a small dock tab with the sender's name and an unread number.
  // It never opens a chat window by itself; clicking the tab opens the conversation.
  const openPopupForIncoming = async (incoming: ChatMessage) => {
    if (!user) return;
    let channel = channelsRef.current.find((c) => c.id === incoming.channelId);
    if (!channel) {
      // A chat we have not loaded yet (e.g. the first message from a new person)
      const loaded = await fetchChannels();
      channel = loaded.find((c) => c.id === incoming.channelId);
    }
    if (!channel || channel.isMuted || channel.isArchived) return;

    const existing = openTabsRef.current.find((t) => t.channelId === channel!.id);
    if (existing) return; // its dock tab already shows the unread number

    const otherMember = channel.type === 'direct' ? channel.members?.find((m) => m.id !== user.id) : null;
    const isTeam = channel.id.startsWith('chan-team-');
    const tab: OpenDockTab = {
      channelId: channel.id,
      title: channel.name,
      subtitle: isTeam ? 'Team Channel' : channel.type === 'direct' ? otherMember?.title || 'Direct Message' : 'Group Chat',
      isDirect: channel.type === 'direct',
      isTeamChannel: isTeam,
      avatarUrl: otherMember?.avatarUrl ?? undefined,
      targetUserId: otherMember?.id,
      minimized: true,
      unread: 1,
    };

    setOpenTabs((prev) => {
      if (prev.some((t) => t.channelId === tab.channelId)) return prev;
      const trimmed = prev.length >= 4 ? prev.slice(prev.length - 3) : prev;
      return [...trimmed, tab];
    });

    // Load the conversation now (without reading it) so it is ready when the tab is clicked
    fetchChannelMessages(tab.channelId, false);
  };

  // Coming back to the window reads the chat that is open in the popup
  useEffect(() => {
    const onFocus = () => {
      const open = openTabsRef.current.find((t) => !t.minimized && t.channelId === activeTabIdRef.current);
      if (open) fetchChannelMessages(open.channelId, true);
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchChannelMessages]);

  // Listen for real-time incoming messages via subscribe()
  useEffect(() => {
    if (!user || isGuest) return;

    const unsubscribe = subscribe((event) => {
      if (event.type === 'chat_message' && event.data) {
        const incoming: ChatMessage = event.data;
        const isFromMe = incoming.senderId === user.id;

        setMessagesByChannel((prev) => {
          const existing = prev[incoming.channelId] || [];
          if (existing.some((m) => m.id === incoming.id)) return prev;
          return {
            ...prev,
            [incoming.channelId]: [...existing, incoming],
          };
        });

        const activeOpenTab = openTabs.find(
          (t) => t.channelId === incoming.channelId && !t.minimized && activeTabId === incoming.channelId
        );

        setChannels((prev) =>
          prev.map((c) => {
            if (c.id !== incoming.channelId) return c;
            return {
              ...c,
              lastMessage: {
                content: incoming.content,
                senderName: incoming.senderName,
                createdAt: incoming.createdAt,
              },
              unreadCount: !isFromMe && !activeOpenTab ? (c.unreadCount || 0) + 1 : 0,
            };
          })
        );

        setOpenTabs((prev) =>
          prev.map((t) => {
            if (t.channelId !== incoming.channelId) return t;
            if (!t.minimized && activeTabId === incoming.channelId) return { ...t, unread: 0 };
            return { ...t, unread: isFromMe ? t.unread : t.unread + 1 };
          })
        );

        // The chat is open in front of the user, so the new message is read right away: tell the server,
        // so the Messages screen, the unread numbers and the sender's ticks agree
        if (!isFromMe && activeOpenTab && token) {
          fetch(`/api/chat/channels/${incoming.channelId}/read`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, ...(user?.id ? { 'x-demo-user-id': user.id } : {}) },
          })
            .then(() => refreshChatUnread())
            .catch(() => {});
        }

        // A new message from someone else opens its chat popup (the chime is played app-wide
        // by the notification layer, so it is not repeated here)
        if (!isFromMe && currentPage !== 'chat') {
          void openPopupForIncoming(incoming);
        }
      } else if (event.type === 'channel_read' && event.data?.userId === user.id) {
        // I read this chat somewhere else (the Messages screen): clear it here too
        const readChannelId = event.data.channelId;
        setChannels((prev) => prev.map((c) => (c.id === readChannelId ? { ...c, unreadCount: 0 } : c)));
        setOpenTabs((prev) => prev.map((t) => (t.channelId === readChannelId ? { ...t, unread: 0 } : t)));
      }
    });

    return () => {
      unsubscribe();
    };
  }, [subscribe, user, isGuest, openTabs, activeTabId, currentPage, token, refreshChatUnread]);

  // Opening a chat (or its messages loading) jumps straight to the last message before anything is
  // drawn; only a single new message arriving while the chat is open scrolls smoothly.
  const lastScrollRef = useRef<{ tab: string | null; count: number }>({ tab: null, count: 0 });
  useLayoutEffect(() => {
    if (!activeTabId) {
      lastScrollRef.current = { tab: null, count: 0 };
      return;
    }
    const count = (messagesByChannel[activeTabId] || []).length;
    const prev = lastScrollRef.current;
    const isOneNewMessage = prev.tab === activeTabId && prev.count > 0 && count === prev.count + 1;
    lastScrollRef.current = { tab: activeTabId, count };
    messagesEndRef.current?.scrollIntoView({ behavior: isOneNewMessage ? 'smooth' : 'auto' });
  }, [activeTabId, messagesByChannel]);

  if (!user || isGuest || currentPage === 'chat') {
    return null;
  }

  const totalUnread = serverUnread;

  const activeTab = openTabs.find((t) => t.channelId === activeTabId && !t.minimized) || null;
  const activeChannel = activeTab
    ? channels.find((c) => c.id === activeTab.channelId) || null
    : null;
  const activeMessages = activeTab ? messagesByChannel[activeTab.channelId] || [] : [];

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0];
    if (!picked || !token) return;
    const [file] = filterUploadableFiles([picked]);
    if (!file) {
      e.target.value = '';
      return;
    }
    setIsUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch('/api/files/upload', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        const uploaded = data.file;
        setPendingAttachments((prev) => [
          ...prev,
          {
            fileName: uploaded?.originalName || file.name,
            fileSize: uploaded?.fileSize || file.size,
            mimeType: uploaded?.mimeType || file.type,
            url: uploaded?.downloadUrl || uploaded?.previewUrl || `/api/files/${uploaded?.id}/download`,
          },
        ]);
      } else {
        const errText = await res.text().catch(() => '');
        let serverMessage = '';
        try {
          serverMessage = JSON.parse(errText).error || '';
        } catch {
          // not JSON — fall back to the HTTP status below
        }
        console.error('File upload failed:', res.status, errText.slice(0, 500));
        alert(
          serverMessage ||
            `Could not upload "${file.name}" (server responded ${res.status}${res.statusText ? ` ${res.statusText}` : ''}).`
        );
      }
    } catch (err) {
      console.error('File upload failed:', err);
    } finally {
      setIsUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeTab || isSending || isViewer) return;
    if (!messageInput.trim() && pendingAttachments.length === 0) return;

    const content = messageInput.trim();
    const attachmentsToSend = [...pendingAttachments];
    setMessageInput('');
    setPendingAttachments([]);
    setIsSending(true);

    try {
      const res = await fetch(`/api/chat/channels/${activeTab.channelId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
        body: JSON.stringify({
          content: content || (attachmentsToSend.length > 0 ? `📎 ${attachmentsToSend[0].fileName}` : ''),
          attachments: attachmentsToSend,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.message) {
          setMessagesByChannel((prev) => {
            const list = prev[activeTab.channelId] || [];
            if (list.some((m) => m.id === data.message.id)) return prev;
            return { ...prev, [activeTab.channelId]: [...list, data.message] };
          });
        }
      }
    } catch (err) {
      console.error('Failed to send message from dock:', err);
    } finally {
      setIsSending(false);
    }
  };

  const handleStartCallFromTab = (mode: 'audio' | 'video') => {
    if (!activeTab || isViewer) return;
    const otherMember =
      activeChannel?.type === 'direct'
        ? activeChannel.members?.find((m) => m.id !== user.id)
        : null;

    window.dispatchEvent(
      new CustomEvent('start-call', {
        detail: {
          channelId: activeTab.channelId,
          channelName: activeTab.title,
          targetUserId: activeTab.targetUserId || otherMember?.id,
          targetUserName: otherMember?.fullName || activeTab.title,
          targetUserAvatar: otherMember?.avatarUrl || activeTab.avatarUrl,
          targetUserRole: otherMember?.title || otherMember?.role || activeTab.subtitle,
          callType: mode,
        },
      })
    );
  };

  const filteredChannels = (channels || []).filter(
    (c) => !c?.isArchived && (c?.name || '').toLowerCase().includes((searchQuery || '').toLowerCase())
  ).sort((a, b) => {
    // pinned first, then newest message on top
    const pin = Number(Boolean(b.isPinned)) - Number(Boolean(a.isPinned));
    if (pin !== 0) return pin;
    const t = (c: any) => Date.parse(c.lastMessage?.createdAt || c.createdAt || '') || 0;
    return t(b) - t(a);
  });

  return (
    <>
      {/* Quick Task Inspection Modal when clicking a task key pill (e.g. TSK-1001, FE-1) */}
      {inspectedTask && (
        <div
          className="fixed inset-0 z-[120] bg-black/50 backdrop-blur-xs flex items-center justify-center p-4"
          onClick={() => setInspectedTask(null)}
        >
          <div
            className="bg-[var(--bg-panel)] border border-[var(--border-color)] rounded-2xl shadow-2xl w-full max-w-md p-5 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-500/15 text-[#026fc1] dark:text-[#38bdf8] text-xs font-bold font-mono">
                  {inspectedTask.code}
                </span>
                <h3 className="text-base font-bold text-[var(--text-primary)] mt-1">
                  {inspectedTask.title}
                </h3>
              </div>
              <button
                onClick={() => setInspectedTask(null)}
                className="p-1.5 rounded-lg hover:bg-[var(--bg-surface)] text-[var(--text-muted)]"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {inspectedTask.description && (
              <p className="text-xs text-[var(--text-secondary)] leading-relaxed line-clamp-4">
                {inspectedTask.description}
              </p>
            )}
            <div className="grid grid-cols-2 gap-2 pt-2 border-t border-[var(--border-color)] text-xs">
              <div>
                <span className="text-[var(--text-muted)] block text-[10px] uppercase font-bold">
                  Status
                </span>
                <span className="font-semibold text-[var(--text-primary)] capitalize">
                  {String(inspectedTask.status || '').replace('_', ' ')}
                </span>
              </div>
              <div>
                <span className="text-[var(--text-muted)] block text-[10px] uppercase font-bold">
                  Priority
                </span>
                <span className="font-semibold text-[var(--text-primary)] capitalize">
                  {inspectedTask.priority}
                </span>
              </div>
              <div>
                <span className="text-[var(--text-muted)] block text-[10px] uppercase font-bold">
                  Assignee
                </span>
                <span className="font-semibold text-[var(--text-primary)]">
                  {inspectedTask.assigneeName || 'Unassigned'}
                </span>
              </div>
              <div>
                <span className="text-[var(--text-muted)] block text-[10px] uppercase font-bold">
                  Board
                </span>
                <span className="font-semibold text-[var(--text-primary)]">
                  {inspectedTask.projectName || 'General'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Persistent Bottom-Right Floating Chat Dock (z-50) */}
      <div className="fixed bottom-4 right-5 z-50 flex items-end gap-2.5 pointer-events-none">
        {/* Minimized Chat Tabs Bar */}
        <div className="flex items-end gap-2 pointer-events-auto">
          {openTabs.map((tab) => {
            const isExpanded = !tab.minimized && activeTabId === tab.channelId;
            if (isExpanded) return null;
            return (
              <div
                key={tab.channelId}
                onClick={() => {
                  setOpenTabs((prev) =>
                    prev.map((t) =>
                      t.channelId === tab.channelId ? { ...t, minimized: false, unread: 0 } : t
                    )
                  );
                  setActiveTabId(tab.channelId);
                  fetchChannelMessages(tab.channelId, true);
                }}
                className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white dark:bg-[#121417] text-[#121417] dark:text-white shadow-xl border border-[#d5d8de] dark:border-white/15 cursor-pointer hover:border-orange-400 dark:hover:border-orange-400/60 transition-colors min-w-[160px] max-w-[230px]"
              >
                {tab.isTeamChannel ? (
                  <Hash className="w-3.5 h-3.5 text-sky-400 shrink-0" />
                ) : (
                  <div className="w-5 h-5 rounded-full bg-[#026fc1] text-white flex items-center justify-center text-[10px] font-bold shrink-0">
                    {tab.title.charAt(0).toUpperCase()}
                  </div>
                )}
                <span className="text-xs font-bold truncate flex-1">{tab.title}</span>
                {Math.max(tab.unread, unreadByChannel[tab.channelId] || 0) > 0 && (
                  <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-extrabold flex items-center justify-center">
                    {Math.max(tab.unread, unreadByChannel[tab.channelId] || 0)}
                  </span>
                )}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenTabs((prev) => prev.filter((t) => t.channelId !== tab.channelId));
                    if (activeTabId === tab.channelId) setActiveTabId(null);
                  }}
                  className="p-0.5 rounded hover:bg-surface-200 dark:hover:bg-white/20 text-surface-400 dark:text-white/70 hover:text-surface-700 dark:hover:text-white"
                  title="Close tab"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>

        {/* Active Open Chat Drawer Window */}
        {activeTab && (
          <div className="pointer-events-auto w-[360px] sm:w-[390px] h-[490px] bg-[var(--bg-panel)] border border-[var(--border-color)] rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-3 duration-200">
            {/* Drawer Header */}
            <div className="px-3.5 py-2.5 bg-gradient-to-r from-[#00203a] to-[#026fc1] text-white flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2.5 min-w-0">
                <button
                  type="button"
                  onClick={() => {
                    setActiveTabId(null);
                    setIsLauncherOpen(true);
                  }}
                  className="p-1 rounded-lg hover:bg-white/15 transition-colors"
                  title="All conversations"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <div className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center font-bold text-xs shrink-0 overflow-hidden">
                  {activeTab.avatarUrl ? (
                    <img
                      src={activeTab.avatarUrl}
                      alt={activeTab.title}
                      className="w-full h-full object-cover"
                    />
                  ) : activeTab.isTeamChannel ? (
                    <Hash className="w-4 h-4 text-white" />
                  ) : (
                    activeTab.title.charAt(0).toUpperCase()
                  )}
                </div>
                <div className="min-w-0">
                  <h4 className="font-bold text-xs truncate leading-tight">
                    {activeTab.isTeamChannel && !activeTab.title.startsWith('#')
                      ? `#${activeTab.title}`
                      : activeTab.title}
                  </h4>
                  <p className="text-[10px] text-white/75 truncate">
                    {activeTab.subtitle || 'Live Collaboration'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-1 shrink-0">
                {!isViewer && (
                  <>
                    <button
                      type="button"
                      onClick={() => handleStartCallFromTab('audio')}
                      className="p-1.5 rounded-lg hover:bg-white/15 text-white/90 hover:text-white transition-colors"
                      title="Escalate to Audio Call"
                    >
                      <Phone className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleStartCallFromTab('video')}
                      className="p-1.5 rounded-lg hover:bg-white/15 text-white/90 hover:text-white transition-colors"
                      title="Escalate to Video Call"
                    >
                      <Video className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => onOpenFullChat(activeTab.channelId)}
                  className="p-1.5 rounded-lg hover:bg-white/15 text-white/90 hover:text-white transition-colors"
                  title="Expand to full /messages page"
                >
                  <Maximize2 className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setOpenTabs((prev) =>
                      prev.map((t) =>
                        t.channelId === activeTab.channelId ? { ...t, minimized: true } : t
                      )
                    )
                  }
                  className="p-1.5 rounded-lg hover:bg-white/15 text-white/90 hover:text-white transition-colors"
                  title="Minimize to dock"
                >
                  <Minus className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setOpenTabs((prev) =>
                      prev.filter((t) => t.channelId !== activeTab.channelId)
                    );
                    setActiveTabId(null);
                  }}
                  className="p-1.5 rounded-lg hover:bg-white/15 text-white/90 hover:text-white transition-colors"
                  title="Close chat"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Messages Stream */}
            <div className="flex-1 overflow-y-auto p-3.5 space-y-2.5 bg-[var(--bg-app)]">
              {activeMessages.length === 0 && loadingChannelIds.includes(activeTab.channelId) ? (
                <div className="h-full flex items-center justify-center text-xs text-[var(--text-muted)]">
                  Loading messages...
                </div>
              ) : activeMessages.length === 0 && loadErrors[activeTab.channelId] ? (
                <div className="h-full flex flex-col items-center justify-center text-center px-4 gap-2">
                  <p className="text-xs font-medium text-rose-500">{loadErrors[activeTab.channelId]}</p>
                  <button
                    type="button"
                    onClick={() => fetchChannelMessages(activeTab.channelId, true)}
                    className="px-3 py-1 rounded-lg text-[11px] font-bold bg-[#026fc1] text-white"
                  >
                    Try again
                  </button>
                </div>
              ) : activeMessages.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center text-[var(--text-muted)] px-4">
                  <Sparkles className="w-8 h-8 mb-2 opacity-40 text-[#026fc1]" />
                  <p className="text-xs font-medium">No messages yet</p>
                  <p className="text-[11px] opacity-75 mt-0.5">
                     Mention teammates with <code className="font-mono">@name</code> or link tasks like{' '}
                    <code className="font-mono">TSK-1001</code>
                  </p>
                </div>
              ) : (
                activeMessages.map((msg) => {
                  const isMe = msg.senderId === user.id;
                  return (
                    <div
                      key={msg.id}
                      className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}
                    >
                      {!isMe && (
                        <span className="text-[10px] font-semibold text-[var(--text-muted)] mb-0.5 px-1">
                          {msg.senderName}
                        </span>
                      )}
                      <div
                        className={`max-w-[84%] px-3 py-2 rounded-2xl text-xs leading-relaxed shadow-xs ${
                          isMe
                            ? 'bg-[#026fc1] text-white rounded-br-xs'
                            : 'bg-[var(--bg-panel)] text-[var(--text-primary)] border border-[var(--border-color)] rounded-bl-xs'
                        }`}
                      >
                        <p className="whitespace-pre-wrap break-words">
                          <LinkifiedText
                            text={msg.content}
                            mentionNames={(activeChannel?.members || []).map((m) => m?.fullName)}
                            linkClassName={
                              isMe
                                ? 'text-white underline hover:opacity-90 break-all inline-flex items-center gap-0.5 font-semibold'
                                : undefined
                            }
                          />
                        </p>
                        {Array.isArray(msg.attachments) && msg.attachments.length > 0 && (
                          <div className="mt-1.5 space-y-1">
                            {msg.attachments.map((att: any, idx: number) => (
                              <a
                                key={idx}
                                href={att.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`flex items-center gap-1.5 px-2 py-1 rounded text-[11px] font-medium ${
                                  isMe
                                    ? 'bg-white/15 text-white hover:bg-white/25'
                                    : 'bg-[var(--bg-surface)] text-[#026fc1] hover:underline'
                                }`}
                              >
                                <FileText className="w-3 h-3 shrink-0" />
                                <span className="truncate">{att.fileName || 'Attachment'}</span>
                                <ExternalLink className="w-2.5 h-2.5 shrink-0 ml-auto" />
                              </a>
                            ))}
                          </div>
                        )}
                        <div
                          className={`flex items-center justify-end gap-1 mt-1 text-[9px] ${
                            isMe ? 'text-white/75' : 'text-[var(--text-muted)]'
                          }`}
                        >
                          <span>
                            {new Date(msg.createdAt).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </span>
                          {isMe && (
                            <CheckCheck
                              className={`w-3 h-3 ${
                                msg.isSeen ? 'text-sky-200' : 'text-white/50'
                              }`}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Pending File Attachments Bar */}
            {pendingAttachments.length > 0 && (
              <div className="px-3 py-1.5 bg-[var(--bg-surface)] border-t border-[var(--border-color)] flex flex-wrap gap-1.5">
                {pendingAttachments.map((att, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-500/15 text-[#026fc1] text-[11px] font-medium"
                  >
                    <Paperclip className="w-3 h-3" />
                    <span className="truncate max-w-[140px]">{att.fileName}</span>
                    <button
                      type="button"
                      onClick={() =>
                        setPendingAttachments((prev) => prev.filter((_, i) => i !== idx))
                      }
                      className="hover:text-rose-500"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Message Input Bar */}
            {!isViewer ? (
              <form
                onSubmit={handleSendMessage}
                className="p-2.5 bg-[var(--bg-panel)] border-t border-[var(--border-color)] flex items-center gap-1.5"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={handleFileUpload}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingFile}
                  className="p-2 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface)] transition-colors"
                  title="Attach file"
                >
                  <Paperclip className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMessageInput((prev) => `${prev}${prev.endsWith(' ') || !prev ? '@' : ' @'}`);
                    inputRef.current?.focus();
                  }}
                  className="p-2 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface)] transition-colors"
                  title="Mention someone (@)"
                >
                  <AtSign className="w-4 h-4" />
                </button>
                <input
                  ref={inputRef}
                  type="text"
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  placeholder="Message, @mention, or TSK-1001..."
                  className="flex-1 bg-[var(--bg-surface)] border border-[var(--border-color)] rounded-xl px-3 py-2 text-xs text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[#026fc1]"
                />
                <button
                  type="submit"
                  disabled={
                    (!messageInput.trim() && pendingAttachments.length === 0) || isSending
                  }
                  className="w-8 h-8 rounded-xl bg-[#026fc1] hover:bg-[#025da3] disabled:opacity-40 text-white flex items-center justify-center transition-colors shrink-0"
                >
                  <Send className="w-3.5 h-3.5" />
                </button>
              </form>
            ) : (
              <div className="px-3 py-2 bg-[var(--bg-surface)] border-t border-[var(--border-color)] text-center text-[11px] text-[var(--text-muted)]">
                Read-only mode (Viewer role)
              </div>
            )}
          </div>
        )}

        {/* Channel Directory / Launcher Popover */}
        {isLauncherOpen && !activeTab && (
          <div className="pointer-events-auto w-[350px] h-[460px] bg-[var(--bg-panel)] border border-[var(--border-color)] rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-3 duration-200">
            <div className="px-4 py-3 bg-gradient-to-r from-[#00203a] to-[#026fc1] text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                <span className="font-bold text-sm">Team & Direct Messages</span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => onOpenFullChat()}
                  className="p-1.5 rounded-lg hover:bg-white/15 text-white/90"
                  title="Open full /messages view"
                >
                  <Maximize2 className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => setIsLauncherOpen(false)}
                  className="p-1.5 rounded-lg hover:bg-white/15 text-white/90"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="p-2.5 border-b border-[var(--border-color)]">
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-[var(--text-muted)] absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search #team-channels or teammates..."
                  className="w-full pl-8 pr-3 py-1.5 text-xs bg-[var(--bg-surface)] border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[#026fc1]"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto divide-y divide-[var(--border-color)]">
              {filteredChannels.map((chan) => {
                const otherMember =
                  chan.type === 'direct'
                    ? chan.members?.find((m) => m.id !== user.id)
                    : null;
                const isOnline = otherMember ? isUserOnline(otherMember.id) : false;
                const isTeamChan = chan.id.startsWith('chan-team-');

                return (
                  <button
                    key={chan.id}
                    type="button"
                    onClick={() =>
                      openOrFocusTab({
                        channelId: chan.id,
                        title: chan.name,
                        subtitle: isTeamChan
                          ? 'Team Channel'
                          : chan.type === 'direct'
                          ? otherMember?.title || 'Direct Message'
                          : 'Group Chat',
                        isDirect: chan.type === 'direct',
                        isTeamChannel: isTeamChan,
                        avatarUrl: otherMember?.avatarUrl,
                        targetUserId: otherMember?.id,
                      })
                    }
                    className="w-full px-3.5 py-2.5 flex items-center gap-3 hover:bg-[var(--bg-surface)] transition-colors text-left"
                  >
                    <div className="relative shrink-0">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#026fc1] to-[#00203a] text-white flex items-center justify-center font-bold text-xs">
                        {isTeamChan ? (
                          <Hash className="w-4 h-4" />
                        ) : chan.type === 'direct' ? (
                          chan.name.charAt(0).toUpperCase()
                        ) : (
                          <Users className="w-4 h-4" />
                        )}
                      </div>
                      {chan.type === 'direct' && (
                        <span
                          className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-[var(--bg-panel)] ${
                            isOnline ? 'bg-emerald-500' : 'bg-slate-400'
                          }`}
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-[var(--text-primary)] truncate">
                          {isTeamChan && !chan.name.startsWith('#')
                            ? `#${chan.name}`
                            : chan.name}
                        </span>
                        {chan.lastMessage && (
                          <span className="text-[10px] text-[var(--text-muted)] shrink-0 ml-1">
                            {new Date(chan.lastMessage.createdAt).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-[var(--text-muted)] truncate mt-0.5">
                        {chan.lastMessage ? chan.lastMessage.content : 'Start a conversation'}
                      </p>
                    </div>
                    {Math.max(chan.unreadCount || 0, unreadByChannel[chan.id] || 0) > 0 && (
                      <span className="min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-rose-500 text-white rounded-full flex items-center justify-center">
                        {Math.max(chan.unreadCount || 0, unreadByChannel[chan.id] || 0)}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Main Floating Dock Launcher Button */}
        <button
          type="button"
          onClick={() => {
            if (activeTab) {
              setActiveTabId(null);
            } else {
              setIsLauncherOpen((prev) => !prev);
            }
          }}
          className="pointer-events-auto relative w-14 h-14 rounded-full bg-gradient-to-br from-[#2b8fe0] to-[#026fc1] dark:from-[#1f2329] dark:to-[#121417] dark:border-2 dark:border-[#2b8fe0] text-white dark:text-[#38a3ee] shadow-xl hover:shadow-2xl hover:scale-105 active:scale-95 transition-all flex items-center justify-center shrink-0"
          title="Open Chat Dock"
          aria-label={totalUnread > 0 ? `Open chats, ${totalUnread} unread` : 'Open chats'}
        >
          {/* Two overlapping speech bubbles */}
          <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none" aria-hidden="true">
            <path
              d="M9.5 3C5.36 3 2 5.9 2 9.5c0 1.6.67 3.07 1.78 4.2-.2 1.05-.7 1.97-1.28 2.6a.4.4 0 0 0 .38.65c1.3-.2 2.45-.75 3.3-1.45.97.37 2.04.5 3.32.5 4.14 0 7.5-2.9 7.5-6.5S13.64 3 9.5 3Z"
              fill="currentColor"
            />
            <path
              d="M22 14.5c0-2.5-2.1-4.5-4.8-4.9.02.27.04.55.04.83 0 4.1-3.4 7.4-7.7 7.7C10.6 19.4 12.2 20 14 20c.9 0 1.7-.12 2.4-.35.6.5 1.4.86 2.3 1a.3.3 0 0 0 .28-.48c-.4-.45-.7-1.05-.85-1.75C19.2 17.5 22 16.2 22 14.5Z"
              fill="currentColor"
              fillOpacity="0.85"
            />
          </svg>
          {totalUnread > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 rounded-full bg-rose-500 text-white text-[11px] font-extrabold flex items-center justify-center shadow-md border-2 border-[var(--bg-app)]">
              {totalUnread > 99 ? '99+' : totalUnread}
            </span>
          )}
        </button>
      </div>
    </>
  );
};
export default FloatingChatPopup;
